import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = (import.meta.env.VITE_SITE_URL || 'https://yoybuy.cyou').replace(/\/$/, '');
const pagePath = '/yoybuy-spreadsheet-sizes';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Таблица размеров в YOYbuy Sheet: как не ошибиться при заказе одежды и обуви из Китая — полное руководство',
  description: 'Всё о размерах одежды и обуви при заказе из Китая через yoybuy spreadsheet. Как конвертировать размеры, измерять параметры и гарантированно получать подходящие вещи.',
  author: { '@type': 'Organization', name: 'YOYbuy Sheet' },
  publisher: {
    '@type': 'Organization',
    name: 'YOYbuy Sheet',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/67a09e300463e31c534fce7fbe7473f7.jpeg',
    },
  },
  datePublished: '2026-07-05',
  dateModified: '2026-07-05',
  url: `${siteUrl}${pagePath}`,
  image: 'https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20photography%20of%20a%20tailor%20measuring%20tape%20elegantly%20draped%20next%20to%20a%20neatly%20folded%20beige%20linen%20shirt%20and%20a%20small%20notebook%20with%20handwritten%20numbers%2C%20on%20a%20light%20wooden%20surface%2C%20soft%20natural%20daylight%20from%20a%20window%2C%20warm%20neutral%20cream%20tones%2C%20fashion%20tailoring%20concept%2C%20editorial%20photography%2C%20clean%20aesthetic&width=1400&height=700&seq=article-sizes-hero-2026&orientation=landscape',
  inLanguage: 'ru',
  isAccessibleForFree: true,
};

export default function SizesArticle() {
  return (
    <>
      <JsonLd data={articleSchema} />
      <Navbar />
      <main className="pt-20 md:pt-24">
        <article className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-6 md:px-10">
            <div className="max-w-3xl mx-auto">
              <header className="mb-10">
                <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
                  <Link to="/" className="hover:text-primary-600 transition-colors">Главная</Link>
                  <span>/</span>
                  <span className="text-foreground-700">Размеры</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight">
                  Таблица размеров в YOYbuy Sheet: как не ошибиться при заказе одежды и обуви из Китая — полное руководство
                </h1>
                <p className="mt-4 text-sm md:text-base text-foreground-500">
                  Всё о размерах одежды и обуви при заказе из Китая через <strong>yoybuy spreadsheet</strong>. Как конвертировать размеры, измерять параметры и гарантированно получать подходящие вещи.
                </p>
                <div className="mt-6 flex items-center gap-4 text-xs text-foreground-400">
                  <span>Обновлено: Июль 2026</span>
                  <span>·</span>
                  <span>Время чтения: 7 минут</span>
                </div>
              </header>

              <div className="mb-10 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Minimalist%20flat%20lay%20photography%20of%20a%20tailor%20measuring%20tape%20elegantly%20draped%20next%20to%20a%20neatly%20folded%20beige%20linen%20shirt%20and%20a%20small%20notebook%20with%20handwritten%20numbers%2C%20on%20a%20light%20wooden%20surface%2C%20soft%20natural%20daylight%20from%20a%20window%2C%20warm%20neutral%20cream%20tones%2C%20fashion%20tailoring%20concept%2C%20editorial%20photography%2C%20clean%20aesthetic&width=1400&height=700&seq=article-sizes-hero-2026&orientation=landscape"
                  alt="Таблица размеров в YOYbuy Sheet"
                  title="YOYbuy Sheet размеры — конвертация размеров одежды и обуви"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom space-y-8 text-sm md:text-base text-foreground-700 leading-relaxed">
                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Почему размеры из Китая — это боль, и как YOYbuy Sheet её решает
                  </h2>
                  <p>
                    Каждый, кто хоть раз заказывал одежду из Китая, сталкивался с проблемой: заказал L — пришёл XS. Китайская размерная сетка существенно отличается от европейской, американской и российской. Например, китайский XL часто соответствует европейскому M. Добавьте к этому различия между разными брендами и продавцами — и получается лотерея.
                  </p>
                  <p>
                    <strong>Yoybuy spreadsheet</strong> решает эту проблему системно. В таблице есть отдельные колонки для ваших реальных измерений: обхват груди, талии, бёдер, длина рукава, длина стопы. Вы не полагаетесь на буквенные обозначения (S, M, L), которые у каждого продавца свои, а указываете фактические сантиметры. Агент, получив такую таблицу, подбирает размер по сетке конкретного продавца — а не по абстрактным стандартам.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Как правильно измерить себя для YOYbuy Spreadsheet
                  </h2>
                  <div className="space-y-4">
                    {[
                      { measure: 'Обхват груди', how: 'Измерьте по самой широкой части груди, держа ленту горизонтально. Не затягивайте слишком туго — лента должна прилегать, но не врезаться. Добавьте 2–4 см для свободного кроя.' },
                      { measure: 'Обхват талии', how: 'Измерьте по естественной линии талии (самое узкое место торса). Для брюк измеряйте там, где будет сидеть пояс. В yoybuy sheet укажите оба значения в примечаниях.' },
                      { measure: 'Обхват бёдер', how: 'Измерьте по самой широкой части бёдер — обычно на 18–22 см ниже талии. Это критически важно для брюк, шорт и юбок.' },
                      { measure: 'Длина рукава', how: 'Измерьте от плечевого шва до запястья, слегка согнув руку. Для курток добавьте 1–2 см — учёт того, что под курткой будет другая одежда.' },
                      { measure: 'Длина стопы', how: 'Встаньте на лист бумаги, обведите стопу, измерьте расстояние от пятки до самого длинного пальца. Добавьте 0.5–1 см для комфорта. В yoybuy sheet это измерение важнее размера EU/US.' },
                    ].map((item) => (
                      <div key={item.measure} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-accent-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.measure}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.how}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <div className="my-8 rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20photography%20of%20a%20tailor%20measuring%20tape%20in%20a%20loose%20curl%20on%20a%20light%20linen%20fabric%20surface%2C%20next%20to%20a%20small%20sketch%20of%20body%20measurements%20on%20paper%2C%20soft%20natural%20lighting%2C%20warm%20cream%20and%20beige%20color%20scheme%2C%20fashion%20design%20and%20tailoring%20concept%2C%20editorial%20photography%2C%20clean%20aesthetic%2C%20high%20detail%20on%20tape%20markings&width=1200&height=600&seq=article-sizes-measuring&orientation=landscape"
                    alt="YOYbuy Sheet — правильное измерение параметров тела"
                    title="Измерение параметров для YOYbuy Spreadsheet"
                    className="w-full h-auto object-cover"
                  />
                </div>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Конвертация размеров в YOYbuy Sheet
                  </h2>
                  <p>
                    В <strong>yoybuy spreadsheet</strong> встроена система конвертации размеров. Вы указываете свой европейский размер в одной колонке, а фактические сантиметры — в другой. Агент, получив таблицу, видит оба значения и подбирает китайский размер, соответствующий вашим реальным параметрам. Это исключает ситуацию «я всегда ношу M, но этот M оказался мал».
                  </p>
                  <div className="p-5 rounded-xl bg-primary-100/70 border border-primary-200 my-6">
                    <h3 className="text-base font-semibold text-foreground-900 mb-2">Золотое правило размеров в yoybuy sheet:</h3>
                    <p className="text-sm text-foreground-600">
                      Никогда не указывайте только буквенный размер. Всегда добавляйте фактические сантиметры. Буквы (S/M/L/XL) — лишь приблизительный ориентир. Сантиметры — точный стандарт, понятный каждому продавцу и агенту.
                    </p>
                  </div>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Размеры для разных категорий товаров
                  </h2>
                  <div className="space-y-4">
                    {[
                      { cat: 'Обувь', rule: 'Длина стопы (см) — единственный надёжный параметр. Игнорируйте размер EU/US при заказе из Китая. Измерьте стопу вечером (к концу дня она немного увеличивается) и добавьте 0.5–1 см. В yoybuy sheet обязательно укажите, если у вас широкая стопа — добавьте пометку «Wide feet».' },
                      { cat: 'Худи и свитеры', rule: 'Ключевые параметры: обхват груди и длина изделия. Худи часто шьются оверсайз, поэтому сравнивайте не с размером, а с параметрами вашего любимого худи. Укажите в таблице желаемую длину и ширину.' },
                      { cat: 'Брюки и шорты', rule: 'Три измерения: обхват талии, обхват бёдер и длина по внутреннему шву. Китайские брюки часто короче европейских — обязательно укажите желаемую длину в сантиметрах в yoybuy spreadsheet.' },
                      { cat: 'Куртки', rule: 'Обхват груди + длина рукава + длина изделия. Для зимних курток укажите, что размер нужен с запасом под свитер. Добавьте 4–6 см к обхвату груди в примечаниях таблицы.' },
                      { cat: 'Футболки', rule: 'Самые forgiving в плане размеров. Достаточно обхвата груди и желаемой длины. Но всё равно указывайте сантиметры, а не буквы — у разных продавцов разная интерпретация размеров.' },
                    ].map((item) => (
                      <div key={item.cat} className="flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-200">
                        <span className="w-1.5 h-1.5 mt-2 rounded-full bg-primary-500 flex-shrink-0"></span>
                        <div>
                          <span className="text-sm font-semibold text-foreground-900">{item.cat}</span>
                          <p className="text-xs text-foreground-500 mt-1">{item.rule}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                    Заключение
                  </h2>
                  <p>
                    Правильно указанные размеры — 90% успеха при заказе одежды из Китая. <strong>Yoybuy spreadsheet</strong> с его системой двойного указания (буквы + сантиметры) сводит риск ошибки практически к нулю. <Link to="/" className="text-primary-600 hover:text-primary-700 underline font-medium">Вернитесь на главную</Link>, выберите категорию и примените эти правила в своём первом заказе. Читайте также: <Link to="/yoybuy-spreadsheet-shoes" className="text-primary-600 hover:text-primary-700 underline font-medium">заказ обуви</Link>, <Link to="/yoybuy-spreadsheet-hoodies" className="text-primary-600 hover:text-primary-700 underline font-medium">заказ худи</Link> и <Link to="/yoybuy-sheet-guide" className="text-primary-600 hover:text-primary-700 underline font-medium">полное руководство</Link>.
                  </p>
                </section>
              </div>

              <div className="mt-12 pt-8 border-t border-background-300">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-sm text-foreground-500">Было полезно? Поделитесь:</p>
                    <div className="flex items-center gap-3 mt-2">
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-twitter-x-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-telegram-line"></i>
                      </a>
                      <a href="#" className="w-9 h-9 rounded-full bg-background-200 flex items-center justify-center text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                        <i className="ri-facebook-line"></i>
                      </a>
                    </div>
                  </div>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Вернуться на главную
                  </Link>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-xl bg-background-100 border border-background-200">
                <p className="text-sm font-semibold text-foreground-900 mb-2">Читайте также:</p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/yoybuy-sheet-guide" className="text-xs text-primary-600 hover:text-primary-700 underline">Руководство по YOYbuy Sheet</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-shoes" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ обуви</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/yoybuy-spreadsheet-hoodies" className="text-xs text-primary-600 hover:text-primary-700 underline">Заказ худи</Link>
                  <span className="text-xs text-foreground-400">·</span>
                  <Link to="/" className="text-xs text-primary-600 hover:text-primary-700 underline">Главная</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}